import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import type { AssignmentResultRow } from "./access-client";
import { AccessErrorAlert } from "./AccessErrorAlert";
import { AssignmentResultList } from "./AssignmentResultList";
import { LocationPicker } from "./LocationPicker";
import { MemberLocationsRemoveList } from "./MemberLocationsRemoveList";
import type { useManageLocationsState } from "./use-manage-locations-state";

type ManageState = ReturnType<typeof useManageLocationsState>;

/** The form: what the member holds (to remove), what can be added, the summary and the error. */
export function ManageLocationsForm({
  state,
  organizationId,
}: {
  readonly state: ManageState;
  readonly organizationId: string;
}) {
  const { t } = useTranslation();
  const { current } = state;
  return (
    <div className="min-w-0 space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("organizationAccess.manageLocations.description")}
      </p>
      <section className="space-y-2">
        <h3 className="text-sm font-medium">
          {t("organizationAccess.manageLocations.currentTitle")}
        </h3>
        <MemberLocationsRemoveList
          locations={current.locations}
          isLoading={current.isLoading}
          isError={current.isError}
          onRetry={current.refetch}
          selected={state.toRemove}
          onToggle={state.toggleRemove}
          disabledIds={state.isPending ? state.activeIds : []}
          testIdPrefix="manage"
        />
      </section>
      <section className="space-y-2">
        <h3 className="text-sm font-medium">{t("organizationAccess.manageLocations.addTitle")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("organizationAccess.manageLocations.addHint")}
        </p>
        <LocationPicker
          organizationId={organizationId}
          selected={state.toAdd}
          onToggle={state.toggleAdd}
          disabledIds={state.activeIds}
          disabled={state.isPending}
          testIdPrefix="manage-add"
        />
      </section>
      {state.removesEverything && (
        <p
          data-testid="manage-remove-all-warning"
          role="status"
          className="rounded-md border border-border bg-muted px-3 py-2 text-sm"
        >
          {t("organizationAccess.manageLocations.willRemoveAll")}
        </p>
      )}
      {!state.nothingToSend && (
        <p className="text-xs text-muted-foreground">
          {t("organizationAccess.manageLocations.summary", {
            add: state.toAdd.size,
            remove: state.toRemove.size,
          })}
        </p>
      )}
      {state.error && <AccessErrorAlert testId="manage-error" message={state.error} />}
    </div>
  );
}

/** What the system did with every requested location. */
export function ManageLocationsResults({
  results,
  names,
}: {
  readonly results: readonly AssignmentResultRow[];
  readonly names: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 space-y-3">
      <p className="text-sm text-muted-foreground">
        {t("organizationAccess.manageLocations.resultIntro")}
      </p>
      <AssignmentResultList results={results} names={names} testId="manage-results" />
    </div>
  );
}

/** Cancel + save while editing; a single Close once the result is shown. */
export function ManageLocationsFooter({
  state,
  onClose,
}: {
  readonly state: ManageState;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  if (state.results) {
    return (
      <Button data-testid="manage-done" onClick={onClose}>
        {t("organizationAccess.manageLocations.done")}
      </Button>
    );
  }
  return (
    <>
      <Button
        data-testid="manage-cancel"
        variant="outline"
        onClick={() => state.handleOpenChange(false)}
        disabled={state.isPending}
      >
        {t("organizationAccess.cancel")}
      </Button>
      <Button
        data-testid="manage-submit"
        onClick={() => void state.submit()}
        disabled={!state.canSubmit}
      >
        {state.isPending
          ? t("organizationAccess.manageLocations.pending")
          : t("organizationAccess.manageLocations.submit")}
      </Button>
    </>
  );
}
